// Action-request execution (T1.3 / REQ-62 / D-5 REVERSED)
//
// The agent NEVER writes — it proposes a typed action_request; the UI renders
// confirm/cancel and, on confirm, performs the write here via the trips REST
// API with the user's own JWT. Shared by every chat surface so proposals
// behave identically everywhere.

import {
  createActivity,
  createTrip,
  deleteActivity,
  updateActivity,
  updateTrip,
} from './api/tripsApi';

export type ActionRequestAction =
  | 'CREATE_TRIP'
  | 'UPDATE_TRIP'
  | 'ADD_ACTIVITIES'
  | 'UPDATE_ACTIVITY'
  | 'DELETE_ACTIVITY';

export interface ActionRequest {
  action: ActionRequestAction;
  trip_id?: string;
  payload: Record<string, any>;
}

export type ActionResolution = 'confirmed' | 'dismissed' | 'failed';

export const ACTION_LABELS: Record<ActionRequestAction, string> = {
  CREATE_TRIP: 'Create this trip',
  UPDATE_TRIP: 'Update this trip',
  ADD_ACTIVITIES: 'Add these activities',
  UPDATE_ACTIVITY: 'Update this activity',
  DELETE_ACTIVITY: 'Remove this activity',
};

// MM-DD-YYYY (+ optional HH:MM) → ISO 8601 for the trips API
export const actionDateToIso = (date?: string, time?: string): string | undefined => {
  if (!date) return undefined;
  const parts = date.split('-');
  if (parts.length !== 3) return undefined;
  const [mm, dd, yyyy] = parts;
  return `${yyyy}-${mm}-${dd}T${time && /^\d{2}:\d{2}$/.test(time) ? time : '09:00'}:00`;
};

// Executes a confirmed proposal. Returns a short user-facing note; throws on failure.
export const executeActionRequest = async (request: ActionRequest): Promise<string> => {
  const payload = request.payload || {};
  switch (request.action) {
    case 'CREATE_TRIP': {
      const created = await createTrip({
        title: payload.title || (payload.destinations || []).join(', ') || 'New trip',
        destinations: payload.destinations || [],
        startDate: actionDateToIso(payload.dates?.startDate)?.slice(0, 10) || '',
        endDate: actionDateToIso(payload.dates?.endDate)?.slice(0, 10) || '',
        budget: payload.budget || undefined,
        description: payload.description || undefined,
      } as any);
      return `Trip "${(created as any)?.title || payload.title || 'trip'}" created`;
    }
    case 'UPDATE_TRIP': {
      if (!request.trip_id) throw new Error('missing trip_id');
      await updateTrip({ id: request.trip_id, ...payload } as any);
      return 'Trip updated';
    }
    case 'ADD_ACTIVITIES': {
      if (!request.trip_id) throw new Error('missing trip_id');
      const activities = payload.activities || [];
      if (!activities.length) throw new Error('no activities in proposal');
      for (const activity of activities) {
        await createActivity(request.trip_id, {
          title: activity.name || activity.title || 'Activity',
          location: activity.location_text || activity.location || '',
          dateTime: actionDateToIso(activity.date, activity.start_time),
        });
      }
      return `Added ${activities.length} ${activities.length === 1 ? 'activity' : 'activities'}`;
    }
    case 'UPDATE_ACTIVITY': {
      if (!request.trip_id || !payload.activity_id) throw new Error('missing trip_id/activity_id');
      const { activity_id, ...changes } = payload;
      await updateActivity(request.trip_id, activity_id, {
        ...(changes.name ? { title: changes.name } : {}),
        ...(changes.location_text ? { location: changes.location_text } : {}),
        ...(changes.date || changes.start_time
          ? { dateTime: actionDateToIso(changes.date, changes.start_time) }
          : {}),
      });
      return 'Activity updated';
    }
    case 'DELETE_ACTIVITY': {
      if (!request.trip_id || !payload.activity_id) throw new Error('missing trip_id/activity_id');
      await deleteActivity(request.trip_id, payload.activity_id);
      return 'Activity removed';
    }
    default:
      throw new Error(`Unknown action ${(request as any).action}`);
  }
};
